import ConfirmModal, { ModalStatuses } from '@/components/dialogs/ConfirmModal';
import useAppDispatch from '@/hooks/useAppDispatch';
import { previewBulkInvite, sendBulkInvite } from '@/redux/actions/users';
import { DEFAULT_TOAST_TIME } from '@/types/consts';
import { IBulkInvitePreview, IParams } from '@/types/models';
import { CircularProgress, Button as MuiButton } from '@mui/material';
import React, { FC, useState } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

const ResendButton = styled(MuiButton)`
  flex-shrink: 0;
  white-space: nowrap;
  height: 100%;
  min-width: 158px;
`;

interface IBulkResendInviteButtonProps {
  searchParams: IParams;
}

const getPreviewText = ({ toSendCount, skippedCount }: IBulkInvitePreview) => {
  const toSendText = `${toSendCount} invitation(s) will be sent.`;
  if (!skippedCount) {
    return toSendText;
  }
  return `${toSendText} ${skippedCount} user(s) skipped: already invited today.`;
};

const BulkResendInviteButton: FC<IBulkResendInviteButtonProps> = ({
  searchParams,
}) => {
  const dispatch = useAppDispatch();
  const [preview, setPreview] = useState<IBulkInvitePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const handleOpen = () => {
    setPreviewLoading(true);
    dispatch(previewBulkInvite(searchParams))
      .unwrap()
      .then((result) => {
        if (result.toSendCount === 0) {
          toast.error(
            result.skippedCount
              ? `Nothing to send: ${result.skippedCount} user(s) were already invited today`
              : 'Nothing to send: no unconfirmed users match the search',
            { position: 'top-right' }
          );
          return;
        }
        setPreview(result);
      })
      .catch(({ error }) =>
        toast.error(error || 'Could not load recipients', {
          position: 'top-right',
        })
      )
      .finally(() => setPreviewLoading(false));
  };

  const handleConfirm = () => {
    setSending(true);
    dispatch(sendBulkInvite(searchParams))
      .unwrap()
      .then(({ sent, skipped, failed }) => {
        const message = `Invitations sent: ${sent}. Skipped: ${skipped}. Failed: ${failed}.`;
        if (failed) {
          toast.warning(message, { position: 'top-right' });
        } else {
          toast.success(message, {
            position: 'top-right',
            autoClose: DEFAULT_TOAST_TIME,
            hideProgressBar: false,
          });
        }
      })
      .catch(({ error }) =>
        toast.error(error || 'Could not send invitations', {
          position: 'top-right',
        })
      )
      .finally(() => {
        setSending(false);
        setPreview(null);
      });
  };

  return (
    <>
      <ResendButton
        color="secondary"
        onClick={handleOpen}
        disabled={previewLoading || sending}
        startIcon={
          previewLoading && <CircularProgress size={16} color="inherit" />
        }
      >
        Resend invitations
      </ResendButton>
      <ConfirmModal
        textConfirm="Send invitations"
        confirmStatus={ModalStatuses.PRIMARY}
        title="Resend invitations"
        text={preview ? getPreviewText(preview) : ''}
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        onConfirm={handleConfirm}
        loading={sending}
        loadingText="Sending..."
      />
    </>
  );
};

export default BulkResendInviteButton;
